// PackageDetailsCard.tsx – "Bartender Package" accordion content: package reference,
// template select, draft item entry, saved items list, platform fee/points, and discount.
import React from 'react';
import { Controller } from 'react-hook-form';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import SearchSelect from '@/components/StyledSelect/SearchSelect';
import ToggleInput from '@/components/toggle/Toggle';
import ItemsList from './BartenderPackageItem';
import PlatformFeeCard from './PlatformFeeCard';
import DiscountCard from './BartenderDiscounts';
import infoIcon from '@/assets/images/info.png';
import { fmt } from '@/helpers/quoteFunctions';
import { PackageProviders, PACKAGE_PROVIDES_MAP } from '../constants/Options';
import type { PackageDetailsCardProps } from '../types/quoteBuilder.types';

export default function PackageDetailsCard({
    control,
    register,
    getValues,
    setValue,
    draft,
    items,
    currency,
    calculatorPackages,
    calcPackage,
    calcPackageTotals,
    includePortableBar,
    onIncludePortableBarChange,
    includeIceAndCooler,
    onIncludeIceAndCoolerChange,
    travelCost,
    eventDistance,
    includeTravelCost,
    onIncludeTravelCostChange,
    includeGratuity,
    onIncludeGratuityChange,
    onCalcPackageChange,
    templateSelect,
    onAddClick,
    onEdit,
    onRemove,
    onCancelEdit,
    platformFeeAmount,
    yourFee,
    subTotalBP,
    afterPlatformFee,
}: PackageDetailsCardProps) {
    // Package gratuity percentage (tipsFee is stored x100, e.g. 1500 = 15%)
    const gratuityPercentage = (calcPackage?.tipsFee || 0) / 100;
    return (
        <div>
            {calculatorPackages.length > 0 && (
                <div className='mb-4 rounded-2xl border border-[#E4E4E7] p-4'>
                    {/* Package select + Total/Profit; wraps on the card width (not the viewport) */}
                    <div className='flex flex-wrap items-end gap-x-8 gap-y-4'>
                        <div className='min-w-0 flex-1 basis-[260px]'>
                            <p className='font-[500] text-[12px] text-[#19191C] mb-2 pl-2'>
                                Select Package for Reference
                            </p>
                            <SearchSelect
                                options={calculatorPackages.map((p) => ({
                                    name: p.name,
                                    value: p.id,
                                }))}
                                value={
                                    calcPackage
                                        ? { name: calcPackage.name, value: calcPackage.id }
                                        : null
                                }
                                onChange={(selectedValue: any) =>
                                    onCalcPackageChange(selectedValue?.value || '')
                                }
                                isSearchable={false}
                                placeholder='Select package'
                                height={40}
                                valueMaxWidth={500}
                            />
                        </div>
                        <div className='ml-auto flex shrink-0 items-center gap-8 rounded-2xl bg-p-gray px-5 py-2'>
                            <div>
                                <p className='text-[12px] text-gray-500'>Total</p>
                                <p className='text-[20px] font-[700] text-[#19191C]'>
                                    {calcPackageTotals
                                        ? fmt(calcPackageTotals.estimatedTotalRevenue)
                                        : '—'}
                                </p>
                            </div>
                            <div>
                                <p className='text-[12px] text-gray-500'>Profit</p>
                                <p className='text-[20px] font-[700] text-[#19191C]'>
                                    {calcPackageTotals
                                        ? fmt(calcPackageTotals.estimatedProfit)
                                        : '—'}
                                </p>
                            </div>
                        </div>
                    </div>
                    <p className='font-[500] text-[12px] text-[#19191C] mt-4 mb-2 pl-2'>
                        Package Add-ons
                    </p>
                    <div className='grid grid-cols-1 gap-x-6 gap-y-3 pl-2 sm:grid-cols-2'>
                        <div className='flex items-center gap-2'>
                            <ToggleInput
                                checked={includePortableBar}
                                onChange={(e: any) =>
                                    onIncludePortableBarChange(e.target.checked)
                                }
                            />
                            <p className='text-[12px] font-[500] whitespace-nowrap'>Portable Bar</p>
                            <p className='text-[12px] text-gray-500 whitespace-nowrap'>
                                {calcPackageTotals
                                    ? fmt(calcPackageTotals.breakdown.mandatoryPortableBarTotal)
                                    : '—'}
                            </p>
                        </div>
                        <div className='flex items-center gap-2'>
                            <ToggleInput
                                checked={includeIceAndCooler}
                                onChange={(e: any) =>
                                    onIncludeIceAndCoolerChange(e.target.checked)
                                }
                            />
                            <p className='text-[12px] font-[500] whitespace-nowrap'>Ice &amp; Cooler</p>
                            <p className='text-[12px] text-gray-500 whitespace-nowrap'>
                                {calcPackageTotals
                                    ? fmt(calcPackageTotals.breakdown.mandatoryIceAndCoolerTotal)
                                    : '—'}
                            </p>
                        </div>
                        {travelCost > 0 && (
                            <div className='flex items-center gap-2'>
                                <ToggleInput
                                    checked={includeTravelCost}
                                    onChange={(e: any) =>
                                        onIncludeTravelCostChange(e.target.checked)
                                    }
                                />
                                <p className='text-[12px] font-[500] whitespace-nowrap'>Travel Cost</p>
                                <p className='text-[12px] text-gray-500 whitespace-nowrap'>
                                    {fmt(travelCost)}
                                    {eventDistance !== null && ` (${eventDistance.toFixed(1)} mi)`}
                                </p>
                            </div>
                        )}
                        {gratuityPercentage > 0 && (
                            <div className='flex items-center gap-2'>
                                <ToggleInput
                                    checked={includeGratuity}
                                    onChange={(e: any) =>
                                        onIncludeGratuityChange(e.target.checked)
                                    }
                                />
                                <p className='text-[12px] font-[500] whitespace-nowrap'>
                                    Gratuity ({gratuityPercentage}%)
                                </p>
                                <p className='text-[12px] text-gray-500 whitespace-nowrap'>
                                    {calcPackageTotals
                                        ? fmt(calcPackageTotals.breakdown.tipsTotal)
                                        : '—'}
                                </p>
                            </div>
                        )}
                    </div>
                    {!calcPackageTotals && calcPackage && (
                        <p className='text-[11px] text-gray-500 mt-2 pl-2'>
                            Enter number of guests and event start/end time to see the estimate.
                        </p>
                    )}
                </div>
            )}
            {templateSelect}
            <div className='flex flex-col gap-4 sm:flex-row sm:gap-8 mt-4'>
                <div className='w-full'>
                    <p className=' text-[12px] font-[500] mb-2 pl-2'>
                        Provided By
                    </p>
                    <Controller
                        control={control}
                        name='draft.provider'
                        render={({ field }) => {
                            return (
                                <SearchSelect
                                    options={PackageProviders}
                                    isSearchable={false}
                                    value={
                                        getValues('draft.provider') && field.value && {
                                            name: PACKAGE_PROVIDES_MAP[field.value]?.name || '',
                                            value: field?.value || '',
                                        }
                                    }
                                    onChange={(selectedValue: any) => {
                                        field.onChange(
                                            selectedValue?.value,
                                        );
                                    }}
                                    height={40}
                                    placeholder='Provides'
                                />
                            );
                        }}
                    />
                </div>
                <div className='w-full'>
                    <p className='text-[12px] font-[500] mb-2 pl-2'>
                        Item
                    </p>
                    <input
                        key={`draft-name-${draft?.provider ?? 'default'}`}
                        type='text'
                        className='w-full px-4 py-2 h-[40px] text-[#000000] font-[400] text-[14px] placeholder:text-[12px] bg-p-gray rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-primary-red'
                        placeholder='Item'
                        maxLength={100}
                        {...register('draft.name')}
                    />
                </div>
                <div className={`w-full ${draft?.provider === 'customerProvide' ? 'hidden' : ''}`}>
                    <p className='text-[12px] font-[500] mb-2 pl-2'>
                        Quantity
                    </p>
                    <input
                        type='number'
                        min={1}
                        max={10000}
                        inputMode="numeric"
                        className='w-full px-4 py-2 h-[40px] text-[#000000] font-[400] text-[14px] placeholder:text-[12px] bg-p-gray rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-primary-red'
                        placeholder='Quantity'
                        onKeyDown={(e) => {
                            if (e.key === '.' || e.key === 'e' || e.key === '-') {
                                e.preventDefault();
                            }
                        }}
                        {...register('draft.qty',
                            {
                                valueAsNumber: true,
                                min: 1,
                                max: 10000,
                                onChange: (e) => {
                                    const value = e.target.value;
                                    // Allow empty while typing
                                    if (value === '') return;
                                    // Block negative numbers
                                    if (Number(value) < 0) {
                                        e.target.value = '';
                                    }
                                    // force integer
                                    if (!Number.isInteger(Number(value))) {
                                        e.target.value = Math.floor(Number(value)).toString();
                                    }
                                    if (Number(value) > 10000) {
                                        e.target.value = '10000';
                                    }
                                }
                            }
                        )}
                    />
                </div>
            </div>
            <div
                className={`flex flex-col gap-4 sm:flex-row sm:gap-6 mt-4 ${draft?.provider === 'customerProvide' ? 'hidden' : ''}`}
            >
                <div className='hidden'>
                    <input
                        type='number'
                        {...register('draft.costPerItem', { valueAsNumber: true })}
                    />
                </div>
                <div className='hidden'>
                    <input
                        type='number'
                        {...register('draft.profitPerItem', { valueAsNumber: true })}
                    />
                </div>
                <div className='w-full'>
                    <p className='text-[12px] font-[500] mb-2 pl-2'>
                        Price Per Item
                    </p>
                    <input
                        type='number'
                        min={0}
                        max={10000}
                        step="0.01"
                        inputMode="numeric"
                        className='w-full px-4 py-2 h-[40px] text-[#000000] font-[400] text-[14px] placeholder:text-[12px] bg-p-gray rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-primary-red'
                        placeholder='Price'
                        {...register('draft.pricePerItem', {
                            valueAsNumber: true,
                            min: 0,
                            max: 10000,
                            onChange: (e) => {
                                const value = e.target.value;
                                if (value === '') return;
                                if (Number(value) < 0) {
                                    e.target.value = '';
                                }
                                if (!/^\d+(\.\d{0,2})?$/.test(value)) {
                                    e.target.value = value.slice(0, -1);
                                    return;
                                }
                                if (Number(value) > 10000) {
                                    e.target.value = '10000';
                                }
                            }
                        })}
                    />
                </div>
                <div className='w-full'>
                    <p className='text-[12px] font-[500] mb-2 pl-2'>
                        Total Item Amount
                    </p>
                    <input
                        type='number'
                        step="0.01"
                        inputMode="numeric"
                        className='w-full px-4 py-2 h-[40px] text-[#000000] font-[400] text-[14px] placeholder:text-[12px] bg-p-gray rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-primary-red '
                        placeholder='Total'
                        {...register('draft.lineTotal')}
                        readOnly
                    />
                </div>
            </div>
            <div className='w-full mt-4'>
                <ItemsList
                    items={items}
                    itemLine={draft}
                    onAddClick={onAddClick}
                    onEdit={onEdit}
                    onRemove={onRemove}
                    currency={currency}
                    editIndex={draft?.editingIndex}
                    onCancelEdit={onCancelEdit}
                />
            </div>
            {/* Platform Fee and Discount in one row */}
            <div className='flex mt-6 justify-between gap-4 mb-3 flex-wrap'>
                <div className='w-full sm:max-w-xs'>
                    <PlatformFeeCard
                        amount={platformFeeAmount}
                    />
                    {/* Your Fee: directly under Platform Fee */}
                    <div className='mt-3 mb-3 w-full max-w-xs'>
                        <div className='w-full rounded-2xl bg-[#F7F8FA] px-4 py-3 sm:px-5 sm:py-3'>
                            <div className='flex items-center justify-between gap-3'>
                                <p className='text-sm font-[400] text-[#19191C]'>Your Fee</p>
                                <p className='text-sm font-[400] text-[#19191C]'>
                                    {fmt(yourFee)}
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* Points: equal to rounded Platform Fee */}
                    <div className='mb-3 mt-10 w-full max-w-xs'>
                        <div className='flex w-full items-center justify-between gap-3 rounded-2xl border border-primary-red bg-[rgb(255,238,231)] px-5 py-3'>
                            <div className='flex items-center gap-1.5'>
                                <p className='text-sm font-semibold text-[#19191C]'>Points</p>
                                <Popover className='relative flex items-center'>
                                    <PopoverButton
                                        type='button'
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                        className='flex items-center outline-none'
                                        aria-label='More info'
                                    >
                                        <img
                                            src={infoIcon}
                                            alt=''
                                            className='h-3.5 w-3.5 opacity-70'
                                        />
                                    </PopoverButton>
                                    <PopoverPanel
                                        anchor={{ to: 'bottom start', gap: 4 }}
                                        className='z-50 w-60 whitespace-normal break-words rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 shadow-lg'
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        1 reward point is equivalent to $1 in platform fees.
                                    </PopoverPanel>
                                </Popover>
                            </div>
                            <p className='text-sm font-semibold text-[#19191C]'>
                                {Math.ceil(platformFeeAmount)}
                            </p>
                        </div>
                    </div>
                </div>
                <div className='w-full sm:max-w-xs'>
                    <Controller
                        control={control}
                        name='discount'
                        render={({ field }) => {
                            const maxDiscount = afterPlatformFee;
                            const clampDiscount = (d: { name: string; amount: number; percentage: number }) => {
                                const raw = d.amount > 0 ? d.amount : (subTotalBP * d.percentage) / 100;
                                if (raw <= maxDiscount) return { name: d.name, amount: String(d.amount), percentage: String(d.percentage) };
                                return { name: d.name, amount: String(maxDiscount), percentage: '' };
                            };
                            return (
                                <DiscountCard
                                    enabled={field.value.enabled}
                                    onEnabledChange={(v) =>
                                        field.onChange({
                                            ...field.value,
                                            enabled: v,
                                        })
                                    }
                                    name={field.value.name}
                                    amount={field.value.amount}
                                    percentage={field.value.percentage}
                                    maxDiscount={maxDiscount}
                                    onApply={(d) => {
                                        const capped = clampDiscount(d);
                                        field.onChange({
                                            ...field.value,
                                            name: capped.name,
                                            amount: capped.amount,
                                            percentage: capped.percentage,
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
